import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  LayoutGrid,
  FileText, // kept for other roles
  BarChart3,
  Users,
  User,
  Wrench,
  ClipboardList, // kept for other roles
  Zap,
  MoreHorizontal,
  X,
  Settings,
  LogOut,
} from 'lucide-react';
import { buildAppPath } from '@/utils/tenant';
import { selectUser } from 'redux/slices/authSlice';
import { userRoleData } from 'redux/slices/userRole';
import { canAccessSection, SECTIONS } from '@/utils/accessControl';
import { performLogout } from '@/utils/logout';
import headerStyles from '@/components/MobileHeader/MobileHeader.module.css';
import AccountQuickPanel from '@/components/account/AccountQuickPanel';

const PipelineSvg = ({ className, strokeWidth, style }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={strokeWidth ? strokeWidth * (16 / 24) : 1.2} strokeLinecap="round" strokeLinejoin="round" className={className} style={style}>
    <path d="M6.66699 4H12.0003" />
    <path d="M8 10.6667H12" />
    <path d="M2 2V12.6667C2 13.0203 2.14048 13.3594 2.39052 13.6095C2.64057 13.8595 2.97971 14 3.33333 14H14" />
    <path d="M5.33301 7.3335H9.99968" />
  </svg>
);

const DocumentSvg = ({ className, strokeWidth, style }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={strokeWidth ? strokeWidth * (16 / 24) : 1.2} strokeLinecap="round" strokeLinejoin="round" className={className} style={style}>
    <path d="M10.0003 1.3335H4.00033C3.6467 1.3335 3.30756 1.47397 3.05752 1.72402C2.80747 1.97407 2.66699 2.31321 2.66699 2.66683V13.3335C2.66699 13.6871 2.80747 14.0263 3.05752 14.2763C3.30756 14.5264 3.6467 14.6668 4.00033 14.6668H12.0003C12.3539 14.6668 12.6931 14.5264 12.9431 14.2763C13.1932 14.0263 13.3337 13.6871 13.3337 13.3335V4.66683L10.0003 1.3335Z" />
    <path d="M9.33301 1.3335V4.00016C9.33301 4.35378 9.47348 4.69292 9.72353 4.94297C9.97358 5.19302 10.3127 5.3335 10.6663 5.3335H13.333" />
    <path d="M6.66634 6H5.33301" />
    <path d="M10.6663 8.6665H5.33301" />
    <path d="M10.6663 11.3335H5.33301" />
  </svg>
);

const ALL_NAV = [
  { label: 'Dashboard', path: '/dashboard', icon: LayoutGrid, roles: ['loanOfficer', 'organization', 'superAdmin', 'branchAdmin', 'loanProcessor', 'consumer'] },
  { label: 'Profile', path: '/profile', icon: User, roles: ['consumer'] },
  { label: 'Loan Info', path: '/loanInformation', icon: FileText, roles: ['loanOfficer', 'organization', 'superAdmin', 'branchAdmin', 'loanProcessor', 'leadGen'] },
  { label: 'Pipeline', path: '/pipeline', icon: ClipboardList, roles: ['loanOfficer', 'organization', 'superAdmin', 'branchAdmin', 'loanProcessor'] },
  { label: 'My Applications', path: '/pipeline', icon: PipelineSvg, roles: ['consumer'] },
  { label: 'Documents', path: '/documents', icon: DocumentSvg, roles: ['consumer'] },
  { label: 'Quick Price', path: '/quickPrice', icon: Zap, roles: ['loanOfficer', 'loanProcessor', 'organization', 'superAdmin', 'branchAdmin'] },
  { label: 'Tools', path: '/tools', icon: Wrench, roles: ['loanOfficer', 'organization', 'superAdmin', 'branchAdmin', 'loanProcessor', 'consumer'] },
  { label: 'Reports', path: '/reports', icon: BarChart3, roles: ['organization', 'superAdmin', 'branchAdmin'] },
  { label: 'Admin', path: '/admin/employees', icon: Users, section: SECTIONS.ADMIN },
  { label: 'Settings', action: 'PROFILE', icon: Settings, roles: ['consumer'] },
];

export default function MobileNavBar({ onChatOpen }) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(selectUser);
  const roleData = useSelector(userRoleData);
  const userRole = roleData?.userRole || user?.role;
  const [moreOpen, setMoreOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 390
  );
  const moreRef = useRef(null);

  const visible = ALL_NAV.filter((item) => {
    if (item.section) return canAccessSection(item.section, user?.originalRole || userRole);
    if (item.roles) return item.roles.includes(userRole);
    return true;
  });

  const isConsumer = userRole === 'consumer';
  const isSmallMobile = viewportWidth <= 390;
  const isVerySmallMobile = viewportWidth <= 360;
  const responsiveWidth = Math.max(320, Math.min(viewportWidth, 430));
  const navScale = Math.max(0.82, Math.min(1, responsiveWidth / 390));
  const navIconSize = Math.round((isVerySmallMobile ? 19 : isSmallMobile ? 20 : 22) * navScale);
  const navItemPadX = Math.round((isVerySmallMobile ? 4 : isSmallMobile ? 6 : 10) * navScale);
  const navLabelMaxWidth = Math.round((isVerySmallMobile ? 56 : isSmallMobile ? 68 : 82) * navScale);
  const navLabelFontSize = isVerySmallMobile ? '8px' : isSmallMobile ? '9px' : '10px';
  const navShellPadY = Math.max(3, Math.round((isVerySmallMobile ? 4 : isSmallMobile ? 5 : 6) * navScale));
  const navShellPadX = Math.max(2, Math.round((isVerySmallMobile ? 2 : isSmallMobile ? 3 : 4) * navScale));
  const chatButtonSize = Math.round((isVerySmallMobile ? 40 : isSmallMobile ? 44 : 50) * navScale);
  const chatIconSize = Math.round((isVerySmallMobile ? 18 : isSmallMobile ? 20 : 24) * navScale);
  const showNavLabel = true;

  const LEFT_COUNT = isConsumer ? 3 : 3;
  const RIGHT_COUNT = isConsumer ? 3 : 2;
  const leftItems = visible.slice(0, LEFT_COUNT);
  const rightItems = visible.slice(LEFT_COUNT, LEFT_COUNT + RIGHT_COUNT);
  const overflowItems = visible.slice(LEFT_COUNT + RIGHT_COUNT);

  const isActive = (path) => path && location.pathname.startsWith(path);

  const handleLogout = async () => {
    setProfileOpen(false);
    await performLogout({ redirect: true });
  };

  useEffect(() => {
    if (!moreOpen) return;
    const handler = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [moreOpen]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const renderItem = (item, compact = false) => {
    const Icon = item.icon;
    const isProfileAction = item.action === 'PROFILE';
    const active = isProfileAction ? profileOpen : isActive(item.path);

    if (compact) {
      if (isProfileAction) {
        return (
          <button
            key={item.label}
            type="button"
            onClick={() => { setMoreOpen(false); setProfileOpen(true); }}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all w-full text-left ${active ? 'bg-[#e8edf5] text-[#22385d]' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            <Icon className="h-[20px] w-[20px] flex-shrink-0" strokeWidth={1.8} />
            <span className={`text-[0.88rem] font-${active ? 'bold' : 'medium'}`}>{item.label}</span>
          </button>
        );
      }
      return (
        <Link
          key={item.path + item.label}
          to={buildAppPath(item.path)}
          onClick={() => setMoreOpen(false)}
          className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${active ? 'bg-[#e8edf5] text-[#22385d]' : 'text-slate-600 hover:bg-slate-50'}`}
        >
          <Icon className="h-[20px] w-[20px] flex-shrink-0" strokeWidth={1.8} />
          <span className={`text-[0.88rem] font-${active ? 'bold' : 'medium'}`}>{item.label}</span>
        </Link>
      );
    }

    if (isProfileAction) {
      return (
        <button
          key={item.label}
          type="button"
          onClick={() => setProfileOpen(true)}
          className={`flex flex-1 basis-0 flex-col items-center justify-center gap-0.5 py-1 rounded-xl transition-all min-w-0 ${active ? 'text-[#22385d]' : 'text-slate-400'}`}
          style={{ paddingLeft: `${navItemPadX}px`, paddingRight: `${navItemPadX}px` }}
        >
          <Icon className="shrink-0" style={{ width: `${navIconSize}px`, height: `${navIconSize}px` }} strokeWidth={1.8} />
          {active && showNavLabel && (
            <span
              className={`font-semibold text-center ${active ? 'text-[#22385d]' : 'text-slate-400'}`}
              style={{
                fontSize: navLabelFontSize,
                lineHeight: 1.05,
                maxWidth: `${navLabelMaxWidth}px`,
                whiteSpace: 'normal',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {item.label}
            </span>
          )}
        </button>
      );
    }

    return (
      <Link
        key={item.path + item.label}
        to={buildAppPath(item.path)}
        className={`flex flex-1 basis-0 flex-col items-center justify-center gap-0.5 py-1 rounded-xl transition-all min-w-0 ${active ? 'text-[#22385d]' : 'text-slate-400'}`}
        style={{ paddingLeft: `${navItemPadX}px`, paddingRight: `${navItemPadX}px` }}
      >
        <Icon className="shrink-0" style={{ width: `${navIconSize}px`, height: `${navIconSize}px` }} strokeWidth={1.8} />
        {active && showNavLabel && (
          <span
            className={`font-semibold text-center ${active ? 'text-[#22385d]' : 'text-slate-400'}`}
            style={{
              fontSize: navLabelFontSize,
              lineHeight: 1.05,
              maxWidth: `${navLabelMaxWidth}px`,
              whiteSpace: 'normal',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {item.label}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 pb-[calc(10px+env(safe-area-inset-bottom))]"
      style={{ paddingLeft: isVerySmallMobile ? '6px' : '8px', paddingRight: isVerySmallMobile ? '6px' : '8px' }}
    >
      {profileOpen && (
        <div className={headerStyles.backdrop} onClick={() => setProfileOpen(false)} />
      )}

      {profileOpen && (
        <div className={headerStyles.profileSheet} style={{ zIndex: 100 }}>
          <div className={headerStyles.sheetHandle} />
          <AccountQuickPanel
            user={user}
            mode="mobile"
            onClose={() => setProfileOpen(false)}
            onNotifications={() => {
              setProfileOpen(false);
              window.dispatchEvent(new CustomEvent('mira:open-notifications'));
            }}
            onSupport={() => {
              setProfileOpen(false);
              navigate(buildAppPath('/help-support'));
            }}
          />
          <button type="button" className={headerStyles.signOutBtn} onClick={handleLogout}>
            <LogOut className={headerStyles.signOutIcon} />
            <span>Sign out</span>
          </button>
        </div>
      )}

      {moreOpen && overflowItems.length > 0 && (
        <div
          ref={moreRef}
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 8px)',
            right: isVerySmallMobile ? '8px' : '12px',
            background: '#fff',
            borderRadius: '18px',
            border: '1px solid rgba(220,225,235,0.8)',
            boxShadow: '0 8px 32px rgba(34,56,93,0.15)',
            padding: '8px',
            minWidth: '160px',
            zIndex: 60,
          }}
        >
          <div className="flex items-center justify-between px-3 pb-2 mb-1 border-b border-slate-100">
            <span className="text-[0.75rem] font-semibold text-slate-400 uppercase tracking-wide">More</span>
            <button type="button" onClick={() => setMoreOpen(false)} className="text-slate-400">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {overflowItems.map((item) => renderItem(item, true))}
        </div>
      )}

      <nav
        style={{
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderRadius: isVerySmallMobile ? '18px' : isSmallMobile ? '20px' : '22px',
          border: '1px solid rgba(220,225,235,0.7)',
          boxShadow: '0 8px 32px rgba(34,56,93,0.13), 0 1px 3px rgba(0,0,0,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: `${navShellPadY}px ${navShellPadX}px`,
          position: 'relative',
          maxWidth: '720px',
          margin: '0 auto',
        }}
      >
        {/* Left side items */}
        <div className="flex items-center flex-1 justify-between gap-1.5 min-w-0">
          {leftItems.map((item) => renderItem(item))}
        </div>

        {/* Center Chat button */}
        <button
          type="button"
          onClick={onChatOpen}
          style={{
            width: `${chatButtonSize}px`,
            height: `${chatButtonSize}px`,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--lh-brand-ink) 0%, var(--lh-brand) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(92,52,143,0.35)',
            border: 'none',
            cursor: 'pointer',
            flexShrink: 0,
            marginBottom: isVerySmallMobile ? '0px' : '2px',
            marginLeft: isVerySmallMobile ? '4px' : '6px',
            marginRight: isVerySmallMobile ? '4px' : '6px',
          }}
          aria-label="Chat"
        >
          <img
            src="/loaninghub-icon.png"
            alt="Chat"
            style={{ width: `${chatIconSize}px`, height: `${chatIconSize}px`, objectFit: 'contain' }}
          />
        </button>

        {/* Right side items + More button */}
        <div className="flex items-center flex-1 justify-between gap-1.5 min-w-0">
          {rightItems.map((item) => renderItem(item))}
          {overflowItems.length > 0 && (
            <button
              type="button"
              onClick={() => setMoreOpen((p) => !p)}
              className={`flex flex-1 basis-0 flex-col items-center justify-center gap-0.5 py-1 rounded-xl transition-all min-w-0 ${moreOpen ? 'text-[#22385d]' : 'text-slate-400'}`}
              style={{ paddingLeft: `${navItemPadX}px`, paddingRight: `${navItemPadX}px` }}
            >
              <MoreHorizontal className="shrink-0" style={{ width: `${navIconSize}px`, height: `${navIconSize}px` }} strokeWidth={1.8} />
              {moreOpen && showNavLabel && (
                <span className="text-[10px] font-semibold leading-tight text-[#22385d] text-center whitespace-normal max-w-[72px]">
                  More
                </span>
              )}
            </button>
          )}
        </div>
      </nav>
    </div>
  );
}
